import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, FormControl, FormControlLabel, InputLabel,
  Link, MenuItem, Select, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow,
  TableSortLabel, TextField, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import BoltIcon from '@mui/icons-material/Bolt';
import { api } from '../api/client';
import type {
  ClearResponse, LogEntry, SortKey, SortStep, ThrowSeverity,
} from '../api/types';
import { sevColor, sevName, srcColor } from '../theme';
import { useLogs, useProviders } from './logs/useLogs';
import { SeveritySelect } from './logs/SeveritySelect';
import { LogEntryDialog } from './logs/LogEntryDialog';
import { usePrefsStore } from '../stores/prefs';

const fmtUk = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London', hour12: false,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit',
});
const fmtTs = (iso: string) => {
  const parts = fmtUk.formatToParts(new Date(iso)).reduce<Record<string, string>>(
    (a, p) => (a[p.type] = p.value, a), {});
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`;
};

export function LogsPage() {
  const prefsLoaded = usePrefsStore((s) => s.loaded);
  const loadPrefs = usePrefsStore((s) => s.load);
  useEffect(() => { if (!prefsLoaded) loadPrefs(); }, [prefsLoaded, loadPrefs]);

  const logs = useLogs();
  const { providers, toggle } = useProviders();

  const [exMessage, setExMessage] = useState('Demo exception fired from /logs');
  const [exSeverity, setExSeverity] = useState<ThrowSeverity>('error');
  const [exStatus, setExStatus] = useState<string>('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState<LogEntry | null>(null);

  const onThrow = async () => {
    setExStatus('Throwing...');
    try {
      const { data } = await api.post('/api/logs/test-exception', { Message: exMessage, Severity: exSeverity });
      setExStatus(`Logged at ${data.severity}: "${data.logged}"`);
      setTimeout(() => logs.load(), 600);
    } catch (ex: unknown) {
      setExStatus('Error: ' + (ex as Error).message);
    }
  };

  const onClearConfirm = async () => {
    setConfirmOpen(false);
    setBusy(true);
    setExStatus('Wiping...');
    try {
      const { data } = await api.post<ClearResponse>('/api/logs/clear');
      const dbR = data.db.ok ? `DB: -${data.db.deleted}` : `DB: ${data.db.error}`;
      const fileR = data.file.ok ? `file: -${data.file.deleted}` : `file: ${data.file.error}`;
      const ai = data.insights.ok
        ? `insights: ${data.insights.operations?.filter((o) => o.accepted).length ?? 0}/${data.insights.operations?.length ?? 0} purges queued`
        : `insights: ${data.insights.error}`;
      setExStatus(`${dbR} | ${fileR} | ${ai}`);
      logs.load();
    } finally {
      setBusy(false);
    }
  };

  const sortedEntries = useMemo(() => {
    const cmp = (a: LogEntry, b: LogEntry): number => {
      for (const { key, dir } of logs.sortChain) {
        const av = a[key], bv = b[key];
        let c = 0;
        if (key === 'severity') c = (av as number) - (bv as number);
        else if (key === 'timestampUtc') c = new Date(av as string).getTime() - new Date(bv as string).getTime();
        else c = String(av ?? '').localeCompare(String(bv ?? ''));
        if (c !== 0) return dir === 'asc' ? c : -c;
      }
      return 0;
    };
    return logs.entries.slice().sort(cmp);
  }, [logs.entries, logs.sortChain]);

  const onHeaderClick = (key: SortKey) => {
    const idx = logs.sortChain.findIndex((s) => s.key === key);
    let next: SortStep[];
    if (idx === -1) {
      const defaultDir = (key === 'timestampUtc' || key === 'severity') ? 'desc' : 'asc';
      next = [...logs.sortChain, { key, dir: defaultDir }];
    } else {
      next = logs.sortChain.map((s, i) => i === idx ? { ...s, dir: s.dir === 'asc' ? 'desc' : 'asc' } : s);
    }
    logs.setSortChain(next);
  };

  const sortStateFor = (key: SortKey): { active: boolean; direction: 'asc' | 'desc'; pos: number | null } => {
    const idx = logs.sortChain.findIndex((s) => s.key === key);
    if (idx === -1) return { active: false, direction: 'asc', pos: null };
    return { active: true, direction: logs.sortChain[idx].dir, pos: logs.sortChain.length > 1 ? idx + 1 : null };
  };

  return (
    <Stack spacing={2}>
      {/* Provider toggles */}
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">Active log providers (live toggle)</Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 1 }}>
            {providers.map((p) => (
              <FormControlLabel
                key={p.name}
                control={
                  <Switch
                    checked={p.enabled} disabled={!p.configured}
                    onChange={(e) => toggle(p.name, e.target.checked)}
                  />
                }
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: srcColor(p.name) }} />
                    <Typography>{p.name}</Typography>
                    {!p.configured && <Typography variant="caption" color="text.secondary">(not configured)</Typography>}
                  </Stack>
                }
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Throw + wipe */}
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">Trigger a logged exception</Typography>
          <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: 'wrap', alignItems: 'center', rowGap: 1 }}>
            <TextField
              size="small" sx={{ flex: 1, minWidth: 280 }}
              label="Message" value={exMessage} onChange={(e) => setExMessage(e.target.value)}
            />
            <SeveritySelect<ThrowSeverity>
              value={exSeverity} onChange={setExSeverity}
              options={[
                { value: 'info', sev: 1, label: 'INFO' },
                { value: 'warn', sev: 2, label: 'WARN' },
                { value: 'error', sev: 3, label: 'ERROR' },
                { value: 'critical', sev: 4, label: 'CRITICAL' },
              ]}
            />
            <Button variant="contained" startIcon={<BoltIcon />} onClick={onThrow} disabled={busy}>Throw &amp; log</Button>
            <Button color="error" variant="contained" startIcon={<DeleteForeverIcon />}
                    onClick={() => setConfirmOpen(true)} disabled={busy}>Delete all logs</Button>
          </Stack>
          {exStatus && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>{exStatus}</Typography>}
        </CardContent>
      </Card>

      {/* Filters + table */}
      <Card>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel id="src-l">Source</InputLabel>
              <Select labelId="src-l" label="Source" value={logs.source} onChange={(e) => logs.setSource(e.target.value as 'all' | 'insights' | 'file' | 'db')}>
                <MenuItem value="all">All sources</MenuItem>
                <MenuItem value="insights">Insights only</MenuItem>
                <MenuItem value="file">File only</MenuItem>
                <MenuItem value="db">DB only</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel id="win-l">Window</InputLabel>
              <Select labelId="win-l" label="Window" value={logs.window} onChange={(e) => logs.setWindow(e.target.value)}>
                <MenuItem value="5m">Last 5m</MenuItem>
                <MenuItem value="15m">Last 15m</MenuItem>
                <MenuItem value="1h">Last 1h</MenuItem>
                <MenuItem value="6h">Last 6h</MenuItem>
                <MenuItem value="24h">Last 24h</MenuItem>
              </Select>
            </FormControl>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25, ml: 0.5 }}>
                Min severity
              </Typography>
              <SeveritySelect<'' | 'info' | 'warn' | 'error'>
                value={logs.severity}
                onChange={logs.setSeverity}
                options={[
                  { value: '', sev: null, label: 'ALL' },
                  { value: 'info', sev: 1, label: 'Info+' },
                  { value: 'warn', sev: 2, label: 'Warn+' },
                  { value: 'error', sev: 3, label: 'Error+' },
                ]}
              />
            </Box>
            <FormControl size="small" sx={{ minWidth: 90 }}>
              <InputLabel id="lim-l">Max</InputLabel>
              <Select labelId="lim-l" label="Max" value={logs.limit} onChange={(e) => logs.setLimit(e.target.value)}>
                <MenuItem value="100">100</MenuItem>
                <MenuItem value="200">200</MenuItem>
                <MenuItem value="500">500</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={<Switch checked={logs.autoRefresh} onChange={(e) => logs.setAutoRefresh(e.target.checked)} />}
              label={<Typography variant="body2">Auto-refresh 10s</Typography>}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" startIcon={<RefreshIcon />} onClick={logs.load}>Refresh</Button>
            <Typography variant="caption" color="text.secondary">
              {logs.count} rows · {logs.windowMinutes}m {logs.loading && <CircularProgress size={10} sx={{ ml: 0.5 }} />}
            </Typography>
          </Stack>

          <Table size="small">
            <TableHead>
              <TableRow>
                {([
                  ['timestampUtc', 'Time (UK)'],
                  ['source', 'Source'],
                  ['severity', 'Sev'],
                  ['category', 'Category'],
                  ['message', 'Message'],
                  ['correlationId', 'Correlation'],
                ] as [SortKey, string][]).map(([k, label]) => {
                  const s = sortStateFor(k);
                  return (
                    <TableCell key={k}>
                      <TableSortLabel
                        active={s.active} direction={s.direction}
                        onClick={() => onHeaderClick(k)}
                      >
                        {label}{s.pos != null && <Typography component="span" variant="caption" sx={{ ml: 0.5, color: 'primary.main', fontWeight: 700 }}>{s.pos}</Typography>}
                      </TableSortLabel>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedEntries.map((e, i) => (
                <TableRow
                  key={`${e.source}-${e.timestampUtc}-${i}`}
                  hover
                  sx={{
                    bgcolor: `${srcColor(e.source)}14`,
                    '& > td:first-of-type': { boxShadow: `inset 4px 0 0 ${srcColor(e.source)}` },
                  }}
                >
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}>{fmtTs(e.timestampUtc)}</TableCell>
                  <TableCell><Chip label={e.source} size="small" sx={{ bgcolor: srcColor(e.source), color: '#fff', fontWeight: 700 }} /></TableCell>
                  <TableCell><Chip label={sevName(e.severity)} size="small" sx={{ bgcolor: sevColor(e.severity), color: '#fff', fontWeight: 700 }} /></TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5 }}>{e.category}</TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12.5, cursor: 'pointer' }} onClick={() => setSelected(e)}>
                    <Link component="span" underline="hover">{e.message}</Link>
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 11.5, color: 'text.secondary' }}>{e.correlationId.slice(0, 12)}</TableCell>
                </TableRow>
              ))}
              {sortedEntries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                    No log entries. (Insights ingest lags 1-3 min.)
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {logs.notes.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {logs.notes.join(' · ')}
            </Alert>
          )}

          <Box sx={{ mt: 1 }}>
            <Link component="button" onClick={() => logs.setSortChain([{ key: 'timestampUtc', dir: 'desc' }])} sx={{ fontSize: 12, color: 'text.secondary' }}>
              Reset sort
            </Link>
          </Box>
        </CardContent>
      </Card>

      <LogEntryDialog entry={selected} onClose={() => setSelected(null)} />

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete all logs?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This wipes the DB and file logs immediately, and queues a purge for AppInsights
            (Microsoft processes asynchronously, may take hours). Cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={onClearConfirm}>Delete all</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
